import React, { FunctionComponent, useEffect, useState, useRef, Fragment } from "react";
import { createRoot, RootType } from "react-dom/client";
import { ConveySocket, ConveyApi, ILang, CNeuralNames, CNueralDialectMap, IConversationEntity } from "@utils";
import { connect } from "react-redux";
import { IRootState } from "@utils";
import { formatPhoneNumber, formatSentence, delay } from "@lib/utils/helpers";
import moment from "moment-timezone";
import Markdown from "react-markdown";
import { Spinner } from "@components/Spinner";
import { store } from "@utils/helpers";
import { ChatInput, ChatInputContainer, ChatButton } from "@components/chatStyles";
import { SwitchWrap, SwitchItem, ActionsContainer, FlexCenterRow, FormInput, SDKButton } from "@components/styles";
import { TranscriptItem } from "./TranscriptionItem";
import Switch from "react-switch";
import { setConversationEntity } from "@actions";
import {
  KeywordsContainer,
  Keyword,
  KeywordTimestamp,
  AudioWrap,
  UploadWrap,
  TranscribeTimestamp,
  TranscribeWrapContainer,
  TranscribeContainer,
  TranscribeText,
  TranscribeTextHeader,
  TranscribeTextIdentity,
  TranscribeWrap,
  SentimentContainer,
  SentimentScore
} from "./transcribeStyles";
import { capitalize } from "@lib/utils/helpers";
// @ts-ignore
import Play from "@assets/icons/play.svg";
// @ts-ignore
import Pause from "@assets/icons/pause.svg";
// @ts-ignore
import Phone from "@assets/icons/hangup.svg";

interface ITranscribeChatProps {
  agencyId: string;
  correlationId: string;
  callerLanguage: string;
  talkback?: boolean;
  showUploader?: boolean;
  conversationId?: string;
  $summaryContainer?: string;
  $sentimentContainer?: string;
  $keywordsContainer?: string;
  neuralName: string;
  app: IRootState["app"];
  agency: IRootState["agency"];
  conversations: IRootState["conversations"];
  callback?: (eventData: Record<any, any>) => void;
  onKeywordCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSentimentScoreCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSummaryCallback?: (eventData: string, isNew: boolean) => void;
  onLiveAudioCallback?: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void;
  recipientAudioFile?: File;
  agentAudioFile?: File;
  dualChannel?: boolean; // should be referred when trying call simulator
  numberCapabilities?: string;
  setConversationEntity: typeof setConversationEntity;
}

const hasCall = (data: IConversationEntity) => {
  console.log({
    data,
    active: data.status === "active",
    channel: data.channel === "voice",
    connected: data.connection_state === "connected"
  });
  return data.status === "active" && data.channel === "voice" && data.connection_state === "connected";
};

const XTranscribeChat: FunctionComponent = ({
  correlationId,
  callback,
  onKeywordCallback,
  onSummaryCallback,
  onSentimentScoreCallback,
  onLiveAudioCallback,
  showUploader,
  callerLanguage,
  app,
  conversationId,
  conversations,
  recipientAudioFile,
  agentAudioFile,
  dualChannel, // should be referred when trying call simulator
  numberCapabilities,
  $summaryContainer,
  setConversationEntity
}: ITranscribeChatProps) => {
  const currentConversation = conversations[conversationId];
  console.log({ currentConversation });
  const { agencyId } = app;
  const [transcripts, setTranscriptions] = useState([]);
  const [terpStatus, setTerpStatus] = useState("");
  const [, setFoundKeywords] = useState({});
  const [showTranslations, setShowTranslations] = useState(false);
  const [terpConnecting, setTerpConnecting] = useState(false);
  const [enableAIVoice, setEnableAIVoice] = useState(currentConversation?.entity?.metadata?.AIVoiceEnabled || false);
  const [showCallControls, setShowCallControls] = useState(
    currentConversation?.entity ? hasCall(currentConversation?.entity) : false
  );
  const foundKeywordsRef = useRef([]);
  const allKeywordsRef = useRef({});
  const hasAudioRef = useRef(false);
  const autoScrollRef = useRef(true);
  const wsRef = useRef({});
  const transcriptsIdentitiesRef = useRef([]);
  const sentimentScoresRef = useRef([]);
  const darkThemeRef = useRef(false);
  const audioArray = useRef<Float32Array | null>(null);
  const transcriptsRef = useRef([]);
  const audioNodeRef = useRef<AudioWorkletNode>(null);
  const audioContextRef = useRef<AudioContext>(null);
  const wsConnectInterval = useRef<NodeJS.Timer | null>(null);
  const summaryRef = useRef<RootType>(null);
  const recipientLanguageRef = useRef("en");
  const fetchConvoSummaryIntervalRef = useRef(null);
  const dualChannelRef = useRef(false);
  const [, setRecipientLanguage] = useState("");
  const [pcmDataUrl, setPcmDataUrl] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#fff");
  const [sentimentScore, setSentimentScore] = useState("");
  const [showAudioPlay, setShowAudioPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pcmLoaded, setPcmLoaded] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [hasAudio, setHasAudio] = useState(false);
  const [, setAudioNodeObj] = useState<AudioWorkletNode>();
  const [agentSelected, setAgentSelected] = useState(false);
  const [callerSelected, setCallerSelected] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const currentLang = JSON.parse(store().getItem("neuralSelection"));
  const [, setLang] = useState<ILang>(
    currentLang
      ? { outputLanguage: currentLang.outputLanguage, neuralName: currentLang.neuralName }
      : {
          outputLanguage: "en-US",
          neuralName: "en-US-JennyNeural"
        }
  );

  const createSpeechSynthesis = async () => {
    setSending(true);
    const response = await ConveyApi.createSpeechSynthesis({
      inputText: text,
      outputLanguage: recipientLanguageRef.current,
      correlationId,
      talkback: "true",
      neuralName: "en-US-AvaMultilingualNeural"
    });
    setText("");
    setSending(false);
    if (response) {
      scrollToBottom();
    }
  };

  const getIdentityLabel = (transcript) => {
    if (!transcript) return;
    return transcript.direction === "outbound"
      ? "Agent" //app.agencyName
      : formatPhoneNumber(decodeURIComponent(transcript.identity));
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      const wrapperEle = document.getElementById(`transcription-container-${correlationId}`);
      if (wrapperEle && autoScrollRef.current) {
        wrapperEle.scrollTo(0, 100000);
      }
    }, 250);
  };

  const findKeywords = (data: Record<any, any>): string[][] => {
    const found = [];
    const dataRoot = data?.event ? data.event : data;
    return dataRoot.channel?.search
      ?.flatMap((searchItem) => {
        if (searchItem.hits.length > 0) {
          return searchItem.hits.map((hit) => {
            if (hit.confidence >= 0.9) {
              if (!found.includes(searchItem.hits[0].snippet)) {
                found.push(searchItem.hits[0].snippet);
                return [searchItem.query, searchItem.hits[0].snippet];
              }
            }
          });
        } else {
          return null;
        }
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (CNueralDialectMap[callerLanguage]) {
      const nueralNameIndex = CNueralDialectMap[callerLanguage];
      const lang = CNeuralNames[nueralNameIndex];
      store().setItem(
        "neuralSelection",
        JSON.stringify({
          language: nueralNameIndex,
          outputLanguage: lang.outputLanguage,
          neuralName: lang.neuralName
        })
      );
      setLang(lang);
    }
  }, [callerLanguage]);

  const getTimestamp = (updatedAt) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedTime = (updatedAt ? moment(updatedAt) : moment()).tz(tz).format("h:mm:ss a");
      return formattedTime;
    } catch (e) {
      return updatedAt;
    }
  };

  useEffect(() => {
    (async () => {
      const keywords = await ConveyApi.getKeywords();
      const keywordsObj = {};
      keywords.forEach((keyword) => {
        keywordsObj[keyword.term] = keyword;
      });
      allKeywordsRef.current = keywordsObj;
      if (!conversationId) {
        return;
      }

      const attachments = await ConveyApi.getConversationAttachments({ conversationId });

      const pcmFile = attachments.find((a) => a.uri.includes(".pcm"));

      if (pcmFile) {
        setPcmDataUrl(pcmFile.uri);
      }

      const messagesResponse = await ConveyApi.getSourceMessages({ source: "transcribe", conversationId });
      transcriptsRef.current = [
        ...transcriptsRef.current,
        ...messagesResponse
          .filter((e) => e.source === "transcribe")
          .map((m) => {
            const isAgent = m.direction === "outbound";
            const formattedIdentity =
              m.direction === "outbound"
                ? "Agent"
                : formatPhoneNumber(
                    m.recipient_label && m.recipient_label != "" ? m.recipient_label : m.recipient_number
                  );

            if (m.direction === "inbound" && m.language !== recipientLanguageRef.current) {
              recipientLanguageRef.current = m.language;
              setRecipientLanguage(m.language);
            }

            if (!transcriptsIdentitiesRef.current.includes(formattedIdentity)) {
              transcriptsIdentitiesRef.current.push(formattedIdentity);
            }

            let highlightWords = [];
            let parsedData = null;
            let createdAt = m.created_at;
            if (m.metadata) {
              try {
                parsedData = typeof m.metadata === "string" ? JSON.parse(m.metadata) : m.metadata;
                if (parsedData?.createdAt) {
                  createdAt = parsedData?.createdAt;
                }
                if (parsedData.provider_data) {
                  if (
                    ["deepgram", "azure"].includes(parsedData.provider_data?.provider) &&
                    parsedData.provider_data?.data
                  ) {
                    highlightWords = findKeywords(parsedData.provider_data?.data);
                  }
                }
              } catch (error) {
                console.log({ error });
                // Handle the error here
              }
            }

            const translated = !!m.body_transl && !!m.language && m.language !== "en" && m.body !== m.body_transl;
            let body = m.message_body;
            const originalBody = m.body;

            if (highlightWords && highlightWords.length > 0) {
              highlightWords.forEach((words) => {
                const { direction: keywordDirection, level } = keywordsObj[words[0]];
                const highlightKeyword =
                  keywordDirection === null ||
                  (isAgent && keywordDirection?.includes("agent")) ||
                  (!isAgent && keywordDirection?.includes("caller"));
                if (highlightKeyword && words[1] && body.toLowerCase().includes(words[1])) {
                  let highlightColor = '#ef5350';
                  if (level === 1) {
                    highlightColor  = '#ef5350';
                  } else if (level === 2) {
                    highlightColor = '#ffb22b';
                  } else if (level === 3) {
                    highlightColor = '#186dde'
                  }
                  const regEx = new RegExp(words[1], "gi");
                  body = formatSentence(body.replace(regEx, `<strong style="color: ${highlightColor}">` + words[1] + "</strong>"));
                  const payload = {
                    term: words[0],
                    data: keywordsObj[words[0]],
                    id: m.message_id,
                    created_at: m.created_at,
                    body: originalBody
                  };
                  foundKeywordsRef.current.push(payload);
                  onKeywordCallback && onKeywordCallback(payload, false);
                }
              });
            }
            setFoundKeywords(foundKeywordsRef.current);
            return {
              id: m.message_id,
              identity: formattedIdentity,
              dialogue: body,
              created_at: createdAt,
              direction: m.direction,
              line: [formattedIdentity, body].join(": "),
              data: translated
                ? {
                    languages: {
                      from: m.language,
                      to: "en"
                    },
                    translations: {
                      from: m.body,
                      to: m.body_transl
                    }
                  }
                : {}
            };
          })
      ];
      setTranscriptions((transcripts) => [...transcriptsRef.current]);
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    })();

    const socketInstance = ConveySocket.init(ConveyApi.readAccessToken());

    const updateConversationHandler = (data) => {
      console.log({ conversationId, dataConvoId: data.conversation_id });
      if (conversationId && data.conversation_id === conversationId) {
        if (data.summary_md) {
          updateSummary(data.summary_md);
          onSummaryCallback && onSummaryCallback(data.summary_md, true);
        }

        console.log("updateConversationHandler", { data });
        if (hasCall(data)) {
          setShowCallControls(true);
        }

        if (data.channel === "voice" && data.status === "closed") {
          setShowCallControls(false);
        }
      }
    };

    const createConversationHandler = (data) => {
      if (conversationId && data.conversation_id === conversationId) {
        if (hasCall(data)) {
          setShowCallControls(true);
        }
        setConversationEntity(data.conversation_id, data);
      }
    };

    const updateConversationRecipientHandler = (data) => {
      if (conversationId && data.conversation_id === conversationId) {
        if (data.role === "terp") {
          if (["connecting", "connected"].includes(data.connection_state)) {
            setTerpStatus(data.connection_state);
          } else {
            setTerpConnecting(false);
            setTerpStatus("");
          }
        }
      }
    };

    (async () => {
      if (!conversationId) {
        return;
      }

      const conversationResponse = await ConveyApi.getConversation({ id: conversationId });
      if ($summaryContainer) {
        const summaryNode = document.getElementById($summaryContainer);
        summaryRef.current = createRoot(summaryNode);
      }
      socketInstance.listen(ConveySocket.CREATE_CONVERSATION, createConversationHandler, true);

      setTimeout(() => {
        if (conversationResponse?.summary_md) {
          updateSummary(conversationResponse?.summary_md);
          onSummaryCallback && onSummaryCallback(conversationResponse?.summary_md, false);
        }
      }, 100);

      socketInstance.listen(ConveySocket.UPDATE_CONVERSATION, updateConversationHandler, true);
      socketInstance.listen(ConveySocket.UPDATE_CONVERSATION_RECIPIENT, updateConversationRecipientHandler, true);
      socketInstance.listen(ConveySocket.CLOSE_CONVERSATION, updateConversationHandler, true);
    })();

    return () => {
      socketInstance.listenOff(ConveySocket.CREATE_CONVERSATION, createConversationHandler);
      socketInstance.listenOff(ConveySocket.UPDATE_CONVERSATION, updateConversationHandler);
      socketInstance.listenOff(ConveySocket.UPDATE_CONVERSATION_RECIPIENT, updateConversationRecipientHandler);
      socketInstance.listenOff(ConveySocket.CLOSE_CONVERSATION, updateConversationHandler);

      if (summaryRef.current) {
        setTimeout(() => {
          summaryRef.current.unmount();
        });
      }
    };
  }, [conversationId]);

  const mergeBuffer = (a, b) => {
    const c = new a.constructor(a.length + b.length);
    c.set(a);
    c.set(b, a.length);

    return c;
  };

  const int16ToFloat32 = (byteArray) => {
    const intArray = new Int16Array(byteArray);
    const float32Array = new Float32Array(intArray.length);

    for (let i = 0; i < intArray.length; i++) {
      float32Array[i] = intArray[i] / 32768.0;
    }

    return float32Array;
  };

  const stereoToMono = (buffer) => {
    const stereoBuffer = new Int16Array(buffer);
    const monoBuffer = new Int16Array(stereoBuffer.length / 2);

    for (let i = 0, j = 0; i < stereoBuffer.length; i += 2, j++) {
      monoBuffer[j] = (stereoBuffer[i] + stereoBuffer[i + 1]) / 2;
    }

    return monoBuffer;
  };

  const convertBlock = (byteArray) => {
    // Create a new Float32Array to hold the float data
    const intArray = new Uint8Array(byteArray);
    const float32Array = new Float32Array(intArray.byteLength / 2);
    // Iterate through the byte array two bytes at a time (for 16-bit samples)
    for (let i = 0; i < intArray.byteLength; i += 2) {
      // Combine the two bytes to form a 16-bit signed integer
      const int16 = (intArray[i + 1] << 8) | (intArray[i] & 0xff);
      // If the integer is negative, handle the sign correctly
      const signedInt16 = int16 >= 0x8000 ? int16 - 0x10000 : int16;
      // Normalize the 16-bit integer to the range [-1.0, 1.0] and store it as a float
      float32Array[i / 2] = signedInt16 / 32768.0;
    }
    return float32Array;
  };

  const openWs = (role) => {
    const wsUrl = `${
      process.env.WS_RTP_HOST
    }?conversationId=${conversationId}&phoneNumber=${correlationId}&primaryRecipient=${correlationId}&agencyId=${
      app.agencyId
    }&source=voice&recipientFlow=true&identity=${
      role === "recipient" ? correlationId : "%2B12222222222"
    }&role=${role}&transcribeProvider=azure&encoding=linear16&langID=true&chunkSize=20&numberChannels=${
      dualChannel ? 2 : 1
    }`;
    // chunkSize=20 <- this is what simulator needs, but daq needs 1000, which is default in RTP index.js
    wsRef.current[role] = new WebSocket(wsUrl);
    wsRef.current[role].binaryType = "arraybuffer";
  };

  const startLangID = async (role: string) => {
    await ConveyApi.updateRecipients({
      number: "12222222222",
      conversationId
    });
    if (!wsRef.current[role]) {
      openWs(role);
    }
    if (wsRef.current[role].readyState === WebSocket.OPEN) {
      initAudio(role);
    } else {
      wsRef.current[role].addEventListener("open", () => {
        initAudio(role);
      });
      wsRef.current[role].addEventListener("close", () => {
        if (!wsConnectInterval.current) {
          wsConnectInterval.current = setInterval(() => {
            if (wsRef.current[role].readyState !== WebSocket.OPEN) {
              openWs(role);
            }
          }, 1000);
        }
      });
      wsRef.current[role].addEventListener("error", () => {
        if (!wsConnectInterval.current) {
          wsConnectInterval.current = setInterval(() => {
            if (wsRef.current[role].readyState !== WebSocket.OPEN) {
              openWs(role);
            }
          }, 1000);
        }
      });
    }
  };

  const startDualLangID = () => {
    startLangID("recipient");
    if (agentAudioFile) {
      startLangID("agent");
    }
  };

  useEffect(() => {
    if (agentAudioFile || recipientAudioFile) {
      setTimeout(() => {
        startDualLangID();
      }, 750);
    }
  }, [agentAudioFile, recipientAudioFile]);

  const initAudio = async (role) => {
    let file = null;
    if (agentAudioFile && role === "agent") {
      file = agentAudioFile;
    } else if (recipientAudioFile && role === "recipient") {
      file = recipientAudioFile;
    } else {
      const files = (document.getElementById(`${role}File`) as HTMLInputElement).files;
      file = files[0];
    }
    if (!file) {
      return;
    }
    const chunksize = dualChannel ? 640 : 320;
    let offset = 0;
    while (offset < file.size) {
      const chunkfile = await file.slice(offset, offset + chunksize);
      // Blob.arrayBuffer() can be polyfilled with a FileReader
      const chunk = await chunkfile.arrayBuffer();
      if (wsRef.current[role].readyState === WebSocket.OPEN) {
        wsRef.current[role].send(chunk);
      }
      await delay(20);
      offset += chunksize;
    }
    wsRef.current[role].close();
  };

  const updateSummary = (summary: string) => {
    if (!summaryRef.current) {
      return;
    }

    summaryRef.current.render(<Markdown>{summary}</Markdown>);
  };

  useEffect(() => {
    const sipSocketInstance = ConveySocket.initSip({ agencyId, correlationId });

    const sipAudioHandler = async (sipData) => {
      const { msg: data, channelsCount: numberChannels } = sipData;
      dualChannelRef.current = numberChannels === 2;
      hasAudioRef.current = true;
      setHasAudio(true);
      audioArray.current = mergeBuffer(
        audioArray.current ? audioArray.current : new Float32Array(0),
        dualChannelRef.current ? int16ToFloat32(stereoToMono(data)) : convertBlock(data)
      );
      const minChannelBytes = dualChannelRef.current ? 320 : 640;
      if (audioArray.current && audioArray.current.byteLength >= minChannelBytes) {
        onLiveAudioCallback && onLiveAudioCallback(audioArray.current, dualChannelRef.current, true);

        if (audioNodeRef.current) {
          audioNodeRef.current.port.postMessage({ message: "audioData", audioData: audioArray.current });
        }
        audioArray.current = new Float32Array(0);
      }
    };

    const sipSentimentHandler = (data) => {
      if (data.hexColor) {
        setBackgroundColor(`#${data.hexColor}`);
      }
      if (data.providerData?.data?.score) {
        setSentimentScore(data.providerData?.data?.score);
        const createdAt = moment.utc().valueOf();
        if (sentimentScoresRef.current.length > 0) {
          const firstScore = sentimentScoresRef.current[0];
          sentimentScoresRef.current.push({
            score: data.providerData?.data?.score,
            timestamp: (createdAt - firstScore.createdAt) / 1000,
            createdAt,
            channel: data.providerData?.data?.channel
          });
        } else {
          sentimentScoresRef.current.push({
            score: data.providerData?.data?.score,
            timestamp: 0,
            createdAt,
            channel: data.providerData?.data?.channel
          });
        }
        onSentimentScoreCallback && onSentimentScoreCallback(data, true);
      }
    };

    const sipRoomHandler = (data) => {
      const {
        direction,
        status,
        transcription,
        translations,
        identity,
        languages,
        id,
        speaker,
        conversationId: transcriptConversationId,
        correlationId: transcriptCorrelationId
      } = data;

      if (conversationId && transcriptConversationId !== conversationId) {
        return;
      }
      if (correlationId && transcriptCorrelationId !== correlationId) {
        return;
      }

      const isAgent = direction === "outbound";

      callback && callback(data);

      let highlightWords = [];

      if (data.providerData) {
        if (["deepgram", "azure", "aws"].includes(data.providerData?.provider)) {
          highlightWords = findKeywords(data.providerData?.data);
        }
      }

      const currentLength = transcriptsRef.current.length;
      let dialogue;
      if (languages) {
        if (languages.to.substring(0, 2) == "en") {
          dialogue = translations.to ? translations.to : transcription;
        }

        if (direction === "inbound" && languages.from !== recipientLanguageRef.current) {
          recipientLanguageRef.current = languages.from;
          setRecipientLanguage(languages.from);
        }
      }
      const originalBody = dialogue;

      const formattedIdentity = formatPhoneNumber(data.identity);

      if (!dialogue || dialogue == "" || (status == "recognizing" && data.confidence < 0.5)) {
        return;
      }

      if (highlightWords && highlightWords.length > 0) {
        highlightWords.forEach((words) => {
          const { direction: keywordDirection, level } = allKeywordsRef.current[words[0]];
          const highlightKeyword =
            keywordDirection === null ||
            (isAgent && keywordDirection?.includes("agent")) ||
            (!isAgent && keywordDirection?.includes("caller"));
          if (highlightKeyword && words[1] && dialogue.toLowerCase().includes(words[1])) {
            let highlightColor = '#ef5350';
            if (level === 1) {
              highlightColor  = '#ef5350';
            } else if (level === 2) {
              highlightColor = '#ffb22b';
            } else if (level === 3) {
              highlightColor = '#186dde'
            }
            const regEx = new RegExp(words[1], "gi");
            dialogue = formatSentence(dialogue.replace(regEx, `<strong style="color: ${highlightColor}">` + words[1] + "</strong>"));
            const keywordPayload = {
              term: words[0],
              data: allKeywordsRef.current[words[0]],
              id,
              created_at: data.createdAt,
              body: originalBody
            };

            const exists = foundKeywordsRef.current.findIndex((t) => t.id === id && t.term === words[0]);
            if (exists === -1) {
              foundKeywordsRef.current.push(keywordPayload);
              onKeywordCallback && onKeywordCallback(keywordPayload, true);
            }
          }
        });
      }
      setFoundKeywords(foundKeywordsRef.current);
      const line = `${formattedIdentity}: ${dialogue}`;

      if (!transcriptsIdentitiesRef.current.includes(formattedIdentity)) {
        transcriptsIdentitiesRef.current.push(formattedIdentity);
      }

      if (currentLength > 0) {
        const lastTranscription = transcriptsRef.current[currentLength - 1];

        if (
          identity == lastTranscription.identity &&
          dialogue.toLowerCase() == lastTranscription.dialogue.toLowerCase()
        ) {
          return;
        }

        if (lastTranscription.status == "recognized") {
          const currentIdIndex = transcriptsRef.current.findIndex((t) => t.id === id);
          if (currentIdIndex !== -1) {
            transcriptsRef.current.splice(currentIdIndex, 1, {
              line,
              dialogue,
              identity: formattedIdentity,
              transcription,
              status,
              direction,
              id,
              created_at: data.createdAt,
              data,
              speaker
            });
          } else {
            transcriptsRef.current = [
              ...transcriptsRef.current,
              {
                line,
                dialogue,
                identity: formattedIdentity,
                transcription,
                status,
                direction,
                id,
                created_at: data.createdAt,
                data,
                speaker
              }
            ];
          }
        } else {
          const currentIdIndex = transcriptsRef.current.findIndex((t) => t.id === id);
          if (currentIdIndex !== -1) {
            transcriptsRef.current.splice(currentIdIndex, 1, {
              line,
              dialogue,
              identity: formattedIdentity,
              transcription,
              status,
              direction,
              id,
              created_at: data.createdAt,
              data,
              speaker
            });
          } else {
            if (identity != lastTranscription.identity) {
              transcriptsRef.current = [
                ...transcriptsRef.current,
                {
                  line,
                  dialogue,
                  identity: formattedIdentity,
                  transcription,
                  status,
                  direction,
                  id,
                  created_at: data.createdAt,
                  data,
                  speaker
                }
              ];
            } else {
              transcriptsRef.current = [
                ...transcriptsRef.current,
                {
                  line,
                  dialogue,
                  identity: formattedIdentity,
                  transcription,
                  status,
                  direction,
                  id,
                  created_at: data.createdAt,
                  data,
                  speaker
                }
              ];
            }
          }
        }
      } else {
        transcriptsRef.current = [
          ...transcriptsRef.current,
          {
            line,
            dialogue,
            identity: formattedIdentity,
            transcription,
            status,
            direction,
            id,
            created_at: data.createdAt,
            data,
            speaker
          }
        ];
      }
      setTranscriptions((transcripts) => [...transcriptsRef.current]);
      setTimeout(() => {
        scrollToBottom();
      }, 250);
    };

    sipSocketInstance
      .listen(ConveySocket.getSipAudio({ agencyId, correlationId }), sipAudioHandler)
      .listen(ConveySocket.getSipSentiment({ agencyId, correlationId }), sipSentimentHandler)
      .listen(ConveySocket.getSipRoom({ agencyId, correlationId }), sipRoomHandler, true);

    return () => {
      sipSocketInstance
        .listenOff(ConveySocket.getSipAudio({ agencyId, correlationId }), sipAudioHandler)
        .listenOff(ConveySocket.getSipSentiment({ agencyId, correlationId }), sipSentimentHandler)
        .listenOff(ConveySocket.getSipRoom({ agencyId, correlationId }), sipRoomHandler);
    };
  }, []);

  const playPCM = async () => {
    if (!audioContextRef.current || !pcmDataUrl) return;

    try {
      const response = await fetch(pcmDataUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioData = int16ToFloat32(stereoToMono(arrayBuffer));

      const numChannels = currentConversation?.entity?.metadata?.channelsCount;

      const sampleRate = 8_000;
      const pcmLength = audioData.byteLength / 2; // Assuming 16-bit PCM

      const audioBuffer = audioContextRef.current.createBuffer(numChannels, pcmLength, sampleRate);

      const data = new Float32Array(audioData);
      audioBuffer.copyToChannel(data, 0);

      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start();
      onLiveAudioCallback && onLiveAudioCallback(audioData, numChannels == 2, false);
      setPcmLoaded(true);
    } catch (error) {
      console.error("Error playing PCM:", error);
    }
  };

  const initAudioContext = async () => {
    if (audioNodeRef.current) {
      return;
    }
    try {
      const audioContext = new AudioContext({ sampleRate: 8000 });
      await audioContext.audioWorklet
        .addModule("https://convey-embed-scriptlets.s3.us-east-2.amazonaws.com/AudioProcessor.js")
        .catch((e) => {
          console.error(e);
          console.trace();
        });
      const audioNode = new AudioWorkletNode(audioContext, "audio-processor");
      audioNode.connect(audioContext.destination);
      audioNodeRef.current = audioNode;
      setAudioNodeObj(audioNode);
      audioContextRef.current = audioContext;
      audioContextRef.current.suspend();
      setShowAudioPlay(true);
    } catch (e) {
      console.log({ e });
    }
  };

  const openUploader = (id: string) => {
    document.getElementById(id).click();
  };

  const handleConnectInterpreter = async () => {
    if (terpStatus === "connecting" || terpStatus === "connected") {
      console.log("CONNECT");
      setTerpConnecting(false);
      await ConveyApi.removeInterpreter({ conversationId, number: "+12222222840" });
    } else {
      console.log("DISCONNET");
      setTerpConnecting(true);
      await ConveyApi.addInterpreter({ conversationId, number: "+12222222840" });
    }
  };

  return (
    <div onClick={initAudioContext}>
      <ActionsContainer style={{ height: 60, padding: "0 10px" }}>
        <FlexCenterRow justify="space-between" style={{ width: "100%" }}>
          <FlexCenterRow justify="start">
            {numberCapabilities &&
              numberCapabilities.includes("terp") &&
              showCallControls &&
              app.initOptions.options.enableInterpreter && (
                <SDKButton
                  data-type="ConnectInterpreter"
                  buttonType="success"
                  onClick={handleConnectInterpreter}
                  disabled={terpConnecting}
                >
                  <Phone /> {terpStatus ? capitalize(terpStatus) : "Terp"}
                </SDKButton>
              )}
            {numberCapabilities &&
              numberCapabilities.includes("terp") &&
              showCallControls &&
              app.initOptions.options.enableVoice2Voice && (
                <SwitchWrap style={{ paddingTop: 20, margin: "0 20px", gap: 10 }}>
                  <SwitchItem style={{ width: "fit-content" }}>
                    <Switch
                      onChange={async (checked) => {
                        if (conversationId) {
                          await ConveyApi.updateMetadata({
                            metadata: {
                              AIVoiceEnabled: checked
                            },
                            objectId: conversationId,
                            objectType: "conversations"
                          });
                          await ConveyApi.toggleVoiceToVoice({
                            enabled: checked ? "true" : "false",
                            correlationId,
                            conversationId
                          });
                        }
                        setEnableAIVoice(!!checked);
                      }}
                      checked={enableAIVoice}
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                    AI Voice
                  </SwitchItem>
                </SwitchWrap>
              )}
            {!app.initOptions.options.disableShowLiveAudio && showAudioPlay && hasAudio && !pcmDataUrl && (
              <AudioWrap
                onClick={(e) => {
                  e.preventDefault();
                  if (audioContextRef.current) {
                    if (isPlaying) {
                      audioContextRef.current.suspend();
                    } else {
                      audioContextRef.current.resume();
                    }
                    setIsPlaying(!isPlaying);
                  }
                }}
              >
                {isPlaying ? <Pause /> : <Play />}
                LIVE
              </AudioWrap>
            )}
            {pcmDataUrl && (
              <>
                <AudioWrap
                  onClick={(e) => {
                    e.preventDefault();
                    if (audioContextRef.current) {
                      if (isPlaying) {
                        audioContextRef.current.suspend();
                      } else {
                        if (pcmLoaded) {
                          audioContextRef.current.resume();
                        } else {
                          playPCM();
                          audioContextRef.current.resume();
                        }
                      }
                      setIsPlaying(!isPlaying);
                    }
                  }}
                >
                  {isPlaying ? <Pause /> : <Play />}
                  PLAY
                </AudioWrap>
              </>
            )}
          </FlexCenterRow>
          <FlexCenterRow justify="start">
            {showUploader && (
              <>
                <UploadWrap>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openUploader("recipientFile");
                    }}
                  >
                    Select Recipient Audio
                  </a>
                  <input
                    id="recipientFile"
                    hidden={true}
                    type="file"
                    onChange={() => {
                      setCallerSelected(true);
                    }}
                    accept="audio/*"
                  />
                </UploadWrap>
                <UploadWrap>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openUploader("agentFile");
                    }}
                  >
                    Select Agent Audio
                  </a>
                  <input
                    id="agentFile"
                    hidden={true}
                    type="file"
                    onChange={() => {
                      setAgentSelected(true);
                    }}
                    accept="audio/*"
                  />
                </UploadWrap>
                {agentSelected && callerSelected && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      startDualLangID();
                    }}
                  >
                    Send Audio
                  </a>
                )}
              </>
            )}
            <SwitchWrap style={{ paddingTop: 10, margin: "0 20px", gap: 10 }}>
              <SwitchItem style={{ width: "fit-content" }}>
                <Switch
                  onChange={(checked) => {
                    setShowTranslations(!!checked);
                  }}
                  checked={showTranslations}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                Show Translations
              </SwitchItem>
              <SwitchItem style={{ width: "fit-content" }}>
                <Switch
                  onChange={(checked) => {
                    autoScrollRef.current = !!checked;
                    setAutoScroll(!!checked);
                  }}
                  checked={autoScroll}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                Autoscroll
              </SwitchItem>
            </SwitchWrap>
            <FormInput
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Search"
              value={search}
              style={{
                boxSizing: "border-box",
                padding: 6,
                width: "100%",
                maxWidth: 600,
                minWidth: 100,
                borderRadius: 4,
                border: "1px solid hsl(0, 0%, 80%)",
                minHeight: 38,
                flex: 1
              }}
            />
          </FlexCenterRow>
        </FlexCenterRow>
      </ActionsContainer>
      <TranscribeWrap style={{ flexDirection: "row", height: "calc(100% - 84px)", top: 62 }}>
        {app.initOptions.options.enableSentimentAnalysis && (
          <SentimentContainer style={{ backgroundColor }}>
            {sentimentScore && <SentimentScore>{sentimentScore}</SentimentScore>}
          </SentimentContainer>
        )}
        <TranscribeWrapContainer>
          <TranscribeContainer id={`transcription-container-${correlationId}`}>
            <div
              style={{
                width: "100%",
                marginLeft: "10px"
              }}
            >
              {transcriptsRef.current.map((transcript, index) => {
                const identityLabel = getIdentityLabel(transcript);
                const previousTranscript = transcriptsRef.current[index - 1];
                const isSameIdentity = previousTranscript
                  ? getIdentityLabel(previousTranscript) === identityLabel
                  : false;
                const isSameSpeaker =
                  transcriptsRef.current[index + 1] && typeof transcriptsRef.current[index + 1]?.speaker !== "undefined"
                    ? transcriptsRef.current[index + 1]?.speaker === transcript.speaker
                    : false;
                const isPreviousTranscriptSentence = (str: string): boolean => {
                  if (!str) return;
                  return [".", "?", "!"].some(suffix => str.endsWith(suffix));
                };
                const sameSpeakerLineBreak = isSameIdentity && transcript.created_at - (previousTranscript?.created_at || 0) >= 1250 && isPreviousTranscriptSentence(previousTranscript?.dialogue?.trim());
                return (
                  (search === "" || transcript.dialogue.includes(search)) && (
                    <Fragment key={transcript.id}>
                      {!isSameIdentity && (
                        <TranscribeTextHeader
                          style={{
                            marginTop: isSameIdentity ? 0 : 24
                          }}
                        >
                          <>
                            <TranscribeTextIdentity>{identityLabel}</TranscribeTextIdentity>
                            <TranscribeTimestamp isAfterIdentity={true}>
                              {getTimestamp(transcript.created_at)}
                            </TranscribeTimestamp>                            
                          </>
                        </TranscribeTextHeader>
                      )}
                      {(sameSpeakerLineBreak && (
                        <TranscribeTimestamp isAfterIdentity={false}>
                          {getTimestamp(transcript.created_at)}
                        </TranscribeTimestamp>
                      ))}
                      <span id={`${transcript.id}`} >
                        <TranscribeText
                          id={`transcribe-msg-${transcript.id}`}
                          direction={transcript.direction}
                          className={`transcript-item transcript-identity-${
                            transcript.identity !== "Agent"
                              ? transcriptsIdentitiesRef.current.indexOf(transcript.identity)
                              : "responder"
                          }`}
                        >
                          <span
                            dangerouslySetInnerHTML={{ __html: transcript.dialogue + " " }}
                            className={
                              typeof transcript.speaker !== "undefined"
                                ? `transcript-speaker-${transcript.speaker}`
                                : ``
                            }
                            style={
                              !isSameIdentity && isSameSpeaker && transcript.direction === "inbound"
                                ? { color: "#000" }
                                : {}
                            }
                          />

                          {showTranslations &&
                            transcript?.data?.languages?.from &&
                            transcript?.data?.translations?.from &&
                            transcript?.data?.languages?.from !== transcript?.data?.languages?.to &&
                            transcript?.data?.translations?.from !== transcript?.data?.translations?.to && (
                              <TranscriptItem transcript={transcript} />
                            )}
                        </TranscribeText>
                      </span>
                    </Fragment>
                  )
                );
              })}
            </div>
          </TranscribeContainer>
          {app.initOptions.options.enableTTS && (
            <ChatInputContainer>
              <ChatInput
                recipient={false}
                onChange={(e) => {
                  setText(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.key === "Enter" || e.keyCode === 13) {
                    createSpeechSynthesis();
                  }
                }}
                value={text}
                disabled={sending}
              />
              <ChatButton
                value="Send"
                data-type="TranscribeTTSSend"
                disabled={sending}
                onClick={() => {
                  createSpeechSynthesis();
                }}
              >
                Send
                {sending && <Spinner />}
              </ChatButton>
            </ChatInputContainer>
          )}
        </TranscribeWrapContainer>
        {app.initOptions.options.enableKeywordBubbles && (
          <KeywordsContainer>
            {foundKeywordsRef.current.map((keyword) => {
              return (
                <Keyword
                  key={[keyword.id, keyword.term].join("")}
                  id={[keyword.id, keyword.term].join("")}
                  level={keyword.data ? keyword.data.level : 1}
                  onClick={() => {
                    const chatMsg = document.getElementById(`transcribe-msg-${keyword.id}`);
                    if (chatMsg) {
                      chatMsg.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
                      chatMsg.classList.add("highlight");
                      setTimeout(() => {
                        chatMsg.classList.remove("highlight");
                      }, 1_000);
                    }
                  }}
                >
                  {keyword.term}
                  <KeywordTimestamp>{getTimestamp(keyword.created_at)}</KeywordTimestamp>
                </Keyword>
              );
            })}
          </KeywordsContainer>
        )}
      </TranscribeWrap>
    </div>
  );
};

const mapStateToProps = ({ app, agency, conversations }: IRootState) => ({
  app,
  agency,
  conversations
});

export const TranscribeChat = connect(mapStateToProps, {
  setConversationEntity
})(XTranscribeChat);
